import { Factory } from "@/data/dao/Factory";
import { SupportDao } from "@/data/dao/service/SupportDao";
import { PricingModel, SaleInquiryState } from "@/data/model/Enums";
import type { ISaleInquiry } from "@/data/model/Support";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { UiNotification } from "@/portal/ui/Notification";

export class SaleInquiryTableController {
    private $element: JQuery;
    private dao: SupportDao;

    private dataTable: DataTable<ISaleInquiry>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<SupportDao>(SupportDao);

        const tableConfig: IDataTableConfig<ISaleInquiry> = {
            columnMapping: [
                {
                    name: i18n("general.fields.state"),
                    property: "state",
                    styles: { width: "13%" },
                    formatter: (m, p) => {
                        let classN = "success";
                        switch (p) {
                            case SaleInquiryState.FirstContact:
                                classN = "danger";
                                break;
                            case SaleInquiryState.InProgress:
                                classN = "warning";
                                break;
                        }

                        return $(
                            '<div class="kt-badge kt-badge--' +
                                classN +
                                ' kt-badge--inline">' +
                                i18n("enums.SaleInquiryState." + SaleInquiryState[p]) +
                                "</div>"
                        );
                    },
                },
                {
                    name: i18n("saleInquiry.fields.priceModel"),
                    property: "pricingModel",
                    styles: { width: "9%" },
                    formatter: (m, p) =>
                        '<div class="kt-badge kt-badge--primary kt-badge--inline">' +
                        i18n("enums.PricingModel." + PricingModel[p]) +
                        "</div>",
                },
                {
                    name: i18n("saleInquiry.fields.received"),
                    property: "received",
                    styles: { width: "11%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("saleInquiry.fields.lastModified"),
                    property: "lastModified",
                    styles: { width: "11%" },
                    formatter: (m, p) =>
                        p === undefined ? i18n("general.timeFormat.notYet") : DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("saleInquiry.fields.person"),
                    property: "userId",
                    styles: { width: "11%" },
                    formatter: (m, p) => {
                        if (p === undefined) return Format.htmlEscape(m.firstName + " " + m.lastName);

                        return $(
                            '<a href="/user/' + p + '">' + Format.htmlEscape(m.firstName + " " + m.lastName) + "</a>"
                        ).click((e) => {
                            e.stopPropagation();
                        });
                    },
                },
                {
                    name: i18n("saleInquiry.fields.company"),
                    property: "company",
                    styles: { width: "9%" },
                    formatter: (m, p) => {
                        if (m.website !== undefined) {
                            return $(
                                '<a href="' +
                                    Format.absoluteUrl(m.website) +
                                    '" target="_blank">' +
                                    Format.htmlEscape(p) +
                                    "</a>"
                            ).click((e) => {
                                e.stopPropagation();
                            });
                        }

                        return p;
                    },
                },
                {
                    name: i18n("saleInquiry.fields.location"),
                    property: "location",
                    styles: { width: "8%" },
                },
                {
                    name: i18n("general.fields.email"),
                    property: "email",
                    formatter: (m, p) => {
                        return $('<a href="mailto:' + p + '" target="_blank">' + Format.htmlEscape(p) + "</a>").click(
                            (e) => {
                                e.stopPropagation();
                            }
                        );
                    },
                },
                {
                    name: i18n("general.fields.phone"),
                    property: "phoneNumber",
                    styles: { width: "9%" },
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: (model, id) => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-text-danger btn-icon btn-icon-md"><i class="la la-trash"></i></a></span>'
                        ).click(() => {
                            UiNotification.confirm(i18n("general.reallyRemove"), undefined, () => {
                                this.dao
                                    .saleInquiryDataSource()
                                    .remove(id)
                                    .then(() => {
                                        UiNotification.showSuccess(i18n("general.successfullyRemoved"));
                                        this.dataTable.loadData();
                                    })
                                    .catch((ex) => {
                                        logError(ex);
                                        UiNotification.showException(i18n("translation.failedDelete"));
                                    });
                            });
                        });
                    },
                },
            ],
            defaultSortAsc: false,
            defaultSortProperty: "received",
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilterFromEnum(
            "state",
            SaleInquiryState,
            "enums.SaleInquiryState",
            i18n("saleInquiry.allStates")
        );
        this.dataTable = new DataTable<ISaleInquiry>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.saleInquiryDataSource(),
            tableConfig
        );

        this.dataTable.listenTo(this.search);
    }
}
